import * as React from 'react'

import { Avatar, Box, Button, CircularProgress } from '@material-ui/core'
import { MarketplaceListing, useListMarketplaceListings } from '../hooks/useListMarketplaceListings'
import { getMarketplaceListingDisplayIcon } from '../getMarketplaceListingDisplayIcon'
import { MarketplaceListingSortBy, MarketplaceListingSortOrder } from '../types'
import { useDeleteMarketplaceListing, useDeleteMarketplaceListingProps } from '../hooks/useDeleteMarketplaceListing'

import { Confirm } from '@vestaboard/installables'
import { CsvExport } from '../../../ui/CsvExport'
import { CsvImportButton } from '../../../ui/CsvImportButton'
import { FormattedDateTime } from '../../formatted-date-time'
import { SearchInput } from '../../../ui/SearchInput'
import { SearchValueType } from '../../../ui/AdvancedSearch'
import { SuperGrid } from '../../../ui/SuperGrid'
import { TruncatedId } from '../../truncated-id'
import moment from 'moment'
import { useHistory } from 'react-router'
import { useUpdateMarketplaceListingV2 } from '../hooks/useUpdateMarketplaceListingV2'
import { replaceEndpointUrls } from '../../../utils/csv'

type MarketplaceListingsListProps = IMarketplaceListingsListProps

interface IMarketplaceListingsListProps {}

interface DeletableMarketplaceListing extends MarketplaceListing {
  setIsDeleting: () => void
}

const columns = [
  {
    title: '',
    width: 50,
    render: (listing: MarketplaceListing) => {
      const iconUrl = getMarketplaceListingDisplayIcon(listing)
      return iconUrl ? <Avatar src={iconUrl} /> : <Avatar>{listing.title?.split('')[0]}</Avatar>
    }
  },
  {
    title: 'Listing ID',
    key: MarketplaceListingSortBy.id,
    width: 150,
    render: (listing: MarketplaceListing) => (
      <TruncatedId value={listing.id} path={`/marketplace-listings/${listing.id}`} />
    )
  },
  {
    title: 'Listing Title',
    key: MarketplaceListingSortBy.title,
    width: 150,
    render: (listing: MarketplaceListing) => listing.title
  },
  {
    title: 'Installable ID',
    key: MarketplaceListingSortBy.installableId,
    width: 150,
    render: (listing: MarketplaceListing) =>
      listing.installableId ? (
        <TruncatedId value={listing.installableId} path={`/installables/${listing.installableId}`} />
      ) : null
  },
  {
    title: 'Installable Title',
    key: MarketplaceListingSortBy.installableTitle,
    width: 150,
    render: (listing: MarketplaceListing) => listing.installableTitle
  },
  {
    title: 'Message Set ID',
    key: MarketplaceListingSortBy.messageSetId,
    width: 150,
    render: (listing: MarketplaceListing) =>
      listing.messageSetId ? (
        <TruncatedId value={listing.messageSetId} path={`/message-sets/${listing.messageSetId}`} />
      ) : null
  },
  {
    title: 'Message Set Title',
    key: MarketplaceListingSortBy.messageSetTitle,
    width: 150,
    render: (listing: MarketplaceListing) => listing.messageSetTitle
  },
  {
    title: 'Release Date',
    width: 120,
    key: MarketplaceListingSortBy.releaseDate,
    render: (listing: MarketplaceListing) =>
      listing.releaseDate ? <FormattedDateTime format='MM/DD/YYYY' value={listing.releaseDate} /> : ''
  },
  {
    title: 'Developer ID',
    key: MarketplaceListingSortBy.developerId,
    width: 150,
    render: (listing: MarketplaceListing) =>
      listing.developerId ? (
        <TruncatedId value={listing.developerId} path={`/developers/${listing.developerId}`} />
      ) : null
  },
  {
    title: 'Developer Name',
    key: MarketplaceListingSortBy.developerName,
    width: 150,
    render: (listing: MarketplaceListing) => listing.developerName
  },
  {
    title: 'Categories',
    key: MarketplaceListingSortBy.categories,
    width: 150,
    render: (listing: MarketplaceListing) => listing.categoryIds.join(', ')
  },
  {
    title: 'Feature Types',
    key: MarketplaceListingSortBy.featureTypes,
    width: 150,
    render: (listing: MarketplaceListing) => listing.featureTypes.join(', ')
  },
  {
    title: 'Subscriptions',
    key: MarketplaceListingSortBy.subscriptionsCount,
    width: 120,
    render: (listing: MarketplaceListing) => (listing.subscriptionsCount || 0).toLocaleString()
  },
  {
    title: 'Subscriptions in Past 7 Days',
    key: MarketplaceListingSortBy.subscriptionsInPast7DaysCount,
    width: 200,
    render: (listing: MarketplaceListing) => (listing.subscriptionsInPast7DaysCount || 0).toLocaleString()
  },
  {
    title: 'Subscriptions in Past 30 Days',
    key: MarketplaceListingSortBy.subscriptionsInPast30DaysCount,
    width: 200,
    render: (listing: MarketplaceListing) => (listing.subscriptionsInPast30DaysCount || 0).toLocaleString()
  },
  {
    title: '',
    width: 100,
    render: (listing: DeletableMarketplaceListing) => (
      <Button variant='contained' color='secondary' onClick={listing.setIsDeleting}>
        Delete
      </Button>
    )
  }
]

const useHashParams = () => {
  const hash = window.location.hash
    .replace('?', '')
    .replace('#', '')
    .split('&')
    .reduce((prev, current) => {
      const [key, value] = current.split('=')
      return {
        ...prev,
        [key]: decodeURI(value)
      }
    }, {}) as any

  if (
    typeof hash.searchValue === 'string' &&
    (hash.searchValue.includes('min') ||
      hash.searchValue.includes('false') ||
      hash.searchValue.includes('true') ||
      hash.searchValue.includes('null'))
  ) {
    hash.searchValue = JSON.parse(hash.searchValue)
  }

  return hash
}

export const MarketplaceListingsList: React.FC<MarketplaceListingsListProps> = props => {
  const hashParams = useHashParams()
  const [perPage] = React.useState(hashParams.perPage ? +hashParams.perPage : 50)
  const [sortBy, setSortBy] = React.useState<MarketplaceListingSortBy>(
    hashParams.sortBy || MarketplaceListingSortBy.title
  )
  const [sortOrder, setSortOrder] = React.useState<MarketplaceListingSortOrder>(MarketplaceListingSortOrder.asc)
  const [searchValue, setSearchValue] = React.useState<SearchValueType>(hashParams.searchValue || null)
  const [page, setPage] = React.useState(hashParams.page ? +hashParams.page : 0)
  const [searchType] = React.useState(hashParams?.searchType || 'terms')
  const [searchModel] = React.useState(hashParams?.searchModel || 'marketplaceListings')
  const input = {
    ...(searchValue || searchValue === false
      ? {
          [searchType]: typeof searchValue === 'string' ? (searchValue || '').trim() : searchValue
        }
      : {}),
    ...(sortBy ? { sortBy } : {}),
    sortOrder
  }
  const history = useHistory()
  const { data, loading, refetch, error } = useListMarketplaceListings({
    ...input,
    perPage,
    page
  })
  const [isDeleting, setIsDeleting] = React.useState<string | null>(null)
  const [deleteMarketplaceListing] = useDeleteMarketplaceListing()
  const [deleteMarketplaceListingProps] = useDeleteMarketplaceListingProps()
  const [updateMarketplaceListing] = useUpdateMarketplaceListingV2()

  React.useEffect(() => {
    const searchValueText = typeof searchValue === 'string' ? searchValue : JSON.stringify(searchValue)
    window.location.hash = `?sortBy=${sortBy}&sortOrder=${sortOrder}&page=${page}&perPage=${perPage}&searchType=${searchType}&searchValue=${
      searchValueText || ''
    }&searchModel=${searchModel}`
  }, [sortBy, sortOrder, page, searchType, searchValue, perPage, searchModel])

  const markeplaceListings = data?.listMarketplaceListings.marketplaceListings || []
  const pagination = data?.listMarketplaceListings?.pagination

  if (!data || loading) return <CircularProgress />

  const _deleteMarketplaceListing = async (id: string) => {
    await deleteMarketplaceListingProps({
      variables: {
        marketplaceListingId: id
      }
    })

    await deleteMarketplaceListing({
      variables: {
        id
      }
    })
    setTimeout(async () => {
      await refetch()
    }, 1000)
  }

  return (
    <>
      {isDeleting ? (
        <Confirm
          title='Are you sure you want to delete this Marketplace Listing?'
          open={true}
          handleClose={() => {
            setIsDeleting(null)
          }}
          handleAccept={async () => {
            await _deleteMarketplaceListing(isDeleting)
            setIsDeleting(null)
          }}></Confirm>
      ) : null}

      <SuperGrid
        title='Marketplace Listings'
        searchType={searchType}
        setSort={({ by, order }) => {
          setSortBy(by)
          setSortOrder(order)
        }}
        centerButtons={
          <>
            <CsvExport
              label='Export'
              paginationKey='input'
              query={useListMarketplaceListings.query}
              variables={{
                input
              }}
              getPagination={({ data }) => ({
                ...data.listMarketplaceListings.pagination,
                page: data.listMarketplaceListings.pagination.page + 1
              })}
              formatData={({ data }) => {
                return data?.listMarketplaceListings.marketplaceListings.map((listing: MarketplaceListing) => ({
                  id: listing.id,
                  title: listing.title,
                  description: listing.description,
                  shortDescription: listing.shortDescription,
                  icon: getMarketplaceListingDisplayIcon(listing),
                  installableId: listing.installableId,
                  installableTitle: listing.installableTitle,
                  messageSetId: listing.messageSetId,
                  noteMessageSetId: listing.listing?.noteMessageSetId,
                  developerId: listing.developerId,
                  developerName: listing.developerName,
                  versionNumber: listing.listing?.version,
                  isFree: listing.listing?.free,
                  isSingleton: listing.listing?.isSingleton,
                  stagingOnly: listing.listing?.stagingOnly,
                  supportsFlagship: listing.supportsFlagship,
                  supportsNote: listing.supportsNote,
                  readyForReactNative: listing.listing?.readyForReactNative,
                  questionAndAnswerId: listing.listing?.questionAndAnswerId,
                  noteQuestionAndAnswerId: listing.listing?.noteQuestionAndAnswerId,
                  previewMessage: listing.listing?.previewMessage,
                  notePreviewMessage: listing.listing?.notePreviewMessage,
                  configuration: listing.listing?.configuration,
                  categoryIds: (listing.categoryIds || []).join(', '),
                  featureTypes: (listing.featureTypes || []).join(', '),
                  created: listing.created ? moment(listing.created).format('YYYY-MM-DD') : null,
                  releaseDate: listing.releaseDate ? moment(listing.releaseDate).format('YYYY-MM-DD') : null,
                  installableCreated: listing.installableCreated
                    ? moment(listing.installableCreated).format('YYYY-MM-DD')
                    : null,
                  deleted: listing.deleted ? 'Yes' : 'No',
                  installationsCount: listing.installationsCount,
                  subscriptionsCount: listing.subscriptionsCount,
                  enabledSubscriptionsCount: listing.enabledSubscriptionsCount,
                  subscriptionsInPast30DaysCount: listing.subscriptionsInPast30DaysCount,
                  subscriptionsInPast7DaysCount: listing.subscriptionsInPast7DaysCount
                }))
              }}
            />
            <CsvImportButton
              mainButtonText='Import'
              mappings={[
                'id',
                'title',
                'description',
                'shortDescription',
                'icon',
                'installableId',
                'messageSetId',
                'noteMessageSetId',
                'developerId',
                'versionNumber',
                'isFree',
                'isSingleton',
                'stagingOnly',
                'supportsFlagship',
                'supportsNote',
                'readyForReactNative',
                'questionAndAnswerId',
                'noteQuestionAndAnswerId',
                'previewMessage',
                'notePreviewMessage',
                'configuration',
                'categoryIds',
                'releaseDate'
              ]}
              handleImport={async records => {
                const errors: string[] = []
                for (const record of records) {
                  // id and installableId are required by the mutation — skip rows missing either
                  if (!record.id || !record.installableId) continue
                  const result = await updateMarketplaceListing({
                    variables: {
                      input: {
                        id: record.id,
                        title: record.title,
                        installableId: record.installableId,
                        description: record.description || undefined,
                        shortDescription: record.shortDescription || undefined,
                        messageSetId: record.messageSetId || undefined,
                        noteMessageSetId: record.noteMessageSetId || undefined,
                        versionNumber: record.versionNumber || undefined,
                        isFree: record.isFree === 'true' ? true : record.isFree === 'false' ? false : undefined,
                        isSingleton:
                          record.isSingleton === 'true' ? true : record.isSingleton === 'false' ? false : undefined,
                        stagingOnly:
                          record.stagingOnly === 'true' ? true : record.stagingOnly === 'false' ? false : undefined,
                        supportsFlagship:
                          record.supportsFlagship === 'true'
                            ? true
                            : record.supportsFlagship === 'false'
                            ? false
                            : undefined,
                        supportsNote:
                          record.supportsNote === 'true' ? true : record.supportsNote === 'false' ? false : undefined,
                        readyForReactNative:
                          record.readyForReactNative === 'true'
                            ? true
                            : record.readyForReactNative === 'false'
                            ? false
                            : undefined,
                        questionAndAnswerId: record.questionAndAnswerId || undefined,
                        noteQuestionAndAnswerId: record.noteQuestionAndAnswerId || undefined,
                        previewMessage: record.previewMessage || undefined,
                        notePreviewMessage: record.notePreviewMessage || undefined,
                        // replaceEndpointUrls returns the same type as its input; string input → string output
                        configuration: record.configuration
                          ? (replaceEndpointUrls(record.configuration) as string)
                          : undefined,
                        categoryIds: record.categoryIds ? record.categoryIds.split(', ') : undefined,
                        releaseAt: (() => {
                          if (!record.releaseDate) return undefined
                          const ts = new Date(record.releaseDate).getTime()
                          return Number.isNaN(ts) ? undefined : ts
                        })()
                      }
                    }
                  })
                  if (result.data?.updateMarketplaceListingV2.__typename === 'MarketplaceListingErrorV2') {
                    errors.push(`${record.id}: ${result.data.updateMarketplaceListingV2.error}`)
                  }
                }
                await refetch()
                if (errors.length > 0) {
                  throw new Error(errors.join('\n'))
                }
              }}
            />
            <Button
              onClick={() => {
                // @ts-ignore
                setSortBy('')
              }}>
              Reset Sorting
            </Button>
          </>
        }
        renderSearch={
          <Box
            style={{
              display: 'flex'
            }}>
            <SearchInput
              value={(searchValue as string) || ''}
              onSearch={value => {
                setPage(0)
                // @ts-ignore
                setSortBy('')
                setSearchValue(value)
              }}
            />
            <Box
              style={{
                width: 12
              }}
            />
            <Button
              variant='contained'
              color='primary'
              onClick={() => {
                history.push(`/new-marketplace-listing`)
              }}>
              Create Listing
            </Button>
          </Box>
        }
        columns={columns}
        rows={markeplaceListings.map(row => ({
          ...row,
          setIsDeleting: () => setIsDeleting(row.id)
        }))}
        sortBy={sortBy}
        sortOrder={sortOrder}
        pagination={pagination}
        setPage={setPage}
        error={!!error}
        loading={loading}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        disableWarnings={searchModel !== 'marketplaceListings'}
      />
    </>
  )
}
